import { useState, useEffect } from "react";
import { MessageSquarePlus, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const EMOJIS = [
  { value: 1, emoji: "😞", label: "Terrible" },
  { value: 2, emoji: "😕", label: "Poor" },
  { value: 3, emoji: "😐", label: "Okay" },
  { value: 4, emoji: "😊", label: "Good" },
  { value: 5, emoji: "🤩", label: "Amazing" },
];

const SIGNUP_BANNER_EVENT = "signup-banner-visibility";

export const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSignupBannerVisible, setIsSignupBannerVisible] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const syncBannerVisibility = () => {
      setIsSignupBannerVisible(document.body.dataset.signupBannerVisible === "true");
    };

    const handleBannerVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ isVisible?: boolean }>;
      setIsSignupBannerVisible(Boolean(customEvent.detail?.isVisible));
    };

    syncBannerVisibility();
    window.addEventListener(SIGNUP_BANNER_EVENT, handleBannerVisibilityChange as EventListener);
    return () => {
      window.removeEventListener(SIGNUP_BANNER_EVENT, handleBannerVisibilityChange as EventListener);
    };
  }, []);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);

    const { error } = await supabase.from("user_feedback" as any).insert({
      user_id: user?.id || null,
      rating,
      message: message.trim() || null,
      page: location.pathname,
    } as any);

    if (error) {
      toast({
        variant: "destructive",
        title: "Oops",
        description: "Couldn't submit feedback. Please try again.",
      });
    } else {
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setRating(null);
        setMessage("");
      }, 2000);
    }
    setSubmitting(false);
  };

  const widgetPositionClass = isSignupBannerVisible ? "bottom-28 right-20 sm:bottom-20" : "bottom-4 right-20";

  if (submitted) {
    return (
      <div className={`fixed z-40 ${widgetPositionClass}`}>
        <div className="w-72 animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-border bg-card p-6 text-center shadow-lg duration-300">
          <div className="mb-2 text-4xl">🎉</div>
          <p className="font-semibold text-foreground">Thank you!</p>
          <p className="mt-1 text-sm text-muted-foreground">Your feedback helps us improve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed z-40 ${widgetPositionClass}`}>
      {isOpen ? (
        <div className="w-80 animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-border bg-card shadow-lg duration-300">
          <div className="flex items-center justify-between p-4 pb-2">
            <h3 className="text-base font-semibold text-foreground">How's your experience?</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              aria-label="Close feedback"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-4 pb-3">
            <div className="flex justify-between gap-1">
              {EMOJIS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => setRating(item.value)}
                  className={cn(
                    "flex min-h-[64px] flex-1 flex-col items-center gap-1 rounded-xl p-2 transition-all duration-200",
                    rating === item.value
                      ? "scale-110 bg-primary/10 ring-2 ring-primary/30"
                      : "hover:bg-secondary active:scale-95"
                  )}
                  aria-label={item.label}
                >
                  <span className="text-2xl">{item.emoji}</span>
                  <span className="text-[10px] font-medium leading-tight text-muted-foreground">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {rating && (
            <div className="animate-in fade-in slide-in-from-top-2 px-4 pb-4 duration-200">
              <Textarea
                placeholder="Tell us more (optional)..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                className="h-20 resize-none border-border/50 bg-secondary/50 text-sm"
              />
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="mt-3 min-h-[44px] w-full gap-2"
                size="sm"
              >
                {submitting ? "Sending..." : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Feedback
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Button
          onClick={() => setIsOpen(true)}
          size="lg"
          className="min-h-[48px] gap-2 rounded-full bg-gradient-to-r from-primary to-primary/90 px-5 shadow-lg hover:from-primary/90 hover:to-primary/80"
        >
          <MessageSquarePlus className="h-5 w-5" />
          <span>Feedback</span>
        </Button>
      )}
    </div>
  );
};